<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, ref, watch } from 'vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import PropertyValuationCasesAPI from 'dashboard/api/propertyValuationCases';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  imovelId: { type: [Number, String], default: '' },
  qprecoInterestId: { type: [Number, String], default: '' },
});

const emit = defineEmits(['updated']);

const cases = ref([]);
const history = ref(null);
const loading = ref(false);
const historyLoading = ref(false);
const saving = ref(false);
const error = ref(false);
const historyError = ref(false);
const reviewReason = ref('');
const brokerOpinionAmount = ref('');
const ownerApprovedAmount = ref('');
const adjustedAmount = ref('');
const adjustmentReason = ref('');
const actualAmount = ref('');
const outcomeDate = ref('');
const outcomeSource = ref('manual_outcome');
const publicLinkExpiresAt = ref('');
const publicLinkUrl = ref('');

const activeCase = computed(() => cases.value[0] || null);
const latestReport = computed(() => activeCase.value?.latest_report || null);
const latestPublicLink = computed(() => latestReport.value?.public_link || {});
const amounts = computed(() => activeCase.value?.amounts || {});
const selections = computed(() => activeCase.value?.selections || {});
const reportPercentiles = computed(() => latestReport.value?.percentiles || {});
const selectionCriteria = computed(
  () =>
    activeCase.value?.selection_criteria ||
    latestReport.value?.selection_criteria ||
    {}
);
const reportComparables = computed(() => {
  const snapshot = latestReport.value?.comparable_snapshot;
  if (Array.isArray(snapshot) && snapshot.length) return snapshot;
  return Array.isArray(selections.value?.items) ? selections.value.items : [];
});
const caseVersionHistory = computed(() =>
  Array.isArray(activeCase.value?.versions) ? activeCase.value.versions : []
);
const latestAdjustment = computed(
  () =>
    activeCase.value?.latest_manual_adjustment ||
    latestReport.value?.latest_manual_adjustment ||
    {}
);
const latestReview = computed(
  () => activeCase.value?.latest_human_review || {}
);
const monthlySeries = computed(() => history.value?.monthly_series || []);
const visibleMonthlySeries = computed(() =>
  monthlySeries.value
    .filter(item => item.cases_count || item.report_versions_count)
    .slice(-6)
);
const versionHistory = computed(() => history.value?.version_history || []);
const backtestSummary = computed(() => history.value?.backtest_summary || {});
const marketCalibration = computed(
  () => history.value?.market_calibration || {}
);
const calibrationReadiness = computed(
  () => marketCalibration.value.readiness || {}
);
const calibrationSummary = computed(
  () => marketCalibration.value.summary || {}
);
const calibrationSegments = computed(
  () => marketCalibration.value.segment_series || []
);
const providerMode = computed(() => history.value?.provider_mode || {});
const autoPublishDisabled = computed(
  () => providerMode.value.auto_publish_price_change === false
);
const canCreate = computed(() => Boolean(props.imovelId) && !saving.value);
const canShareLatestReport = computed(
  () => Boolean(activeCase.value && latestReport.value) && !saving.value
);
const canSubmitAdjustment = computed(
  () =>
    Boolean(
      activeCase.value && adjustedAmount.value && adjustmentReason.value.trim()
    ) && !saving.value
);

const formatCurrency = value => {
  const number = Number(value || 0);
  if (!number) return 'Sem dados';

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(number);
};

const formatPercent = value => {
  const number = Number(value || 0);
  if (!Number.isFinite(number) || !number) return 'Sem dados';
  return `${number.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;
};

const formatDate = value => {
  if (!value) return 'Sem data';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

const formatScore = value => {
  const number = Number(value || 0);
  if (!Number.isFinite(number) || !number) return 'Sem score';
  return `${Math.round(number * 100)}%`;
};

const criteriaEntries = computed(() =>
  [
    ['Modalidade', selectionCriteria.value.modality],
    ['Data-base', selectionCriteria.value.base_date],
    ['Amostra mínima', selectionCriteria.value.min_samples],
    [
      'Janela',
      selectionCriteria.value.max_age_days
        ? `${selectionCriteria.value.max_age_days} dias`
        : null,
    ],
    ['Tolerância área', selectionCriteria.value.area_tolerance],
    ['Delta quartos', selectionCriteria.value.bedroom_delta],
    ['Limite candidatos', selectionCriteria.value.candidate_limit],
  ]
    .filter(
      ([, value]) => value !== null && value !== undefined && value !== ''
    )
    .map(([label, value]) => ({ label, value }))
);

const comparableSource = item =>
  item?.source || item?.source_license || 'Fonte não informada';
const comparableDate = item =>
  item?.event_date || item?.eventDate || item?.selected_at;
const comparablePrice = item =>
  item?.adjusted_price_amount ||
  item?.price_amount ||
  item?.observed_price_amount;
const comparableAdjustment = item =>
  item?.adjustment_total_amount || item?.adjustmentTotalAmount;
const comparableScore = item =>
  item?.comparability_score || item?.comparabilityScore || item?.weight;

const loadCases = async () => {
  if (!props.imovelId) {
    cases.value = [];
    return;
  }

  loading.value = true;
  error.value = false;
  try {
    const { data } = await PropertyValuationCasesAPI.get({
      imovel_id: props.imovelId,
      qpreco_interest_id: props.qprecoInterestId,
      limit: 5,
    });
    cases.value = data.data || [];
  } catch {
    error.value = true;
    cases.value = [];
  } finally {
    loading.value = false;
  }
};

const loadHistory = async () => {
  if (!props.imovelId) {
    history.value = null;
    return;
  }

  historyLoading.value = true;
  historyError.value = false;
  try {
    const { data } = await PropertyValuationCasesAPI.monthlyHistory({
      imovel_id: props.imovelId,
      qpreco_interest_id: props.qprecoInterestId,
      months: 12,
      limit: 12,
    });
    history.value = data;
  } catch {
    historyError.value = true;
    history.value = null;
  } finally {
    historyLoading.value = false;
  }
};

const loadPanel = () => {
  loadCases();
  loadHistory();
};

const createCase = async () => {
  if (!canCreate.value) return;

  saving.value = true;
  try {
    const { data } = await PropertyValuationCasesAPI.create({
      imovel_id: props.imovelId,
      qpreco_interest_id: props.qprecoInterestId,
      purpose: 'cma',
      parameters: { min_samples: 3 },
    });
    cases.value = [data, ...cases.value.filter(item => item.id !== data.id)];
    useAlert('Caso de avaliação criado.');
    emit('updated', data);
    loadHistory();
  } catch {
    useAlert('Não foi possível criar a avaliação agora.');
  } finally {
    saving.value = false;
  }
};

const submitReview = async decision => {
  if (!activeCase.value || saving.value) return;
  saving.value = true;
  try {
    const { data } = await PropertyValuationCasesAPI.review(
      activeCase.value.id,
      {
        decision,
        decision_reason: reviewReason.value || 'revisao humana registrada',
        broker_opinion_amount: brokerOpinionAmount.value,
        owner_approved_amount: ownerApprovedAmount.value,
        metadata: { surface: 'area_qpreco_review_panel' },
      }
    );
    cases.value = [data, ...cases.value.filter(item => item.id !== data.id)];
    reviewReason.value = '';
    useAlert('Revisão de avaliação registrada.');
    emit('updated', data);
    loadHistory();
  } catch {
    useAlert('Não foi possível registrar a revisão.');
  } finally {
    saving.value = false;
  }
};

const submitAdjustment = async () => {
  if (!activeCase.value || !canSubmitAdjustment.value) return;

  saving.value = true;
  try {
    const { data } = await PropertyValuationCasesAPI.adjustment(
      activeCase.value.id,
      {
        adjusted_amount: adjustedAmount.value,
        reason: adjustmentReason.value,
        metadata: { surface: 'area_qpreco_review_panel' },
      }
    );
    cases.value = [data, ...cases.value.filter(item => item.id !== data.id)];
    adjustedAmount.value = '';
    adjustmentReason.value = '';
    useAlert('Ajuste registrado em nova versão do laudo.');
    emit('updated', data);
    loadHistory();
  } catch {
    useAlert('Não foi possível registrar o ajuste.');
  } finally {
    saving.value = false;
  }
};

const submitBacktest = async () => {
  if (!activeCase.value || saving.value) return;
  saving.value = true;
  try {
    const { data } = await PropertyValuationCasesAPI.backtest(
      activeCase.value.id,
      {
        actual_amount: actualAmount.value,
        outcome_date: outcomeDate.value,
        outcome_source: outcomeSource.value,
        metadata: { surface: 'area_qpreco_review_panel' },
      }
    );
    cases.value = [data, ...cases.value.filter(item => item.id !== data.id)];
    actualAmount.value = '';
    useAlert('Backtest registrado.');
    emit('updated', data);
    loadHistory();
  } catch {
    useAlert('Não foi possível registrar o backtest.');
  } finally {
    saving.value = false;
  }
};

const createPublicLink = async () => {
  if (!canShareLatestReport.value) return;

  saving.value = true;
  try {
    const { data } = await PropertyValuationCasesAPI.createPublicLink(
      activeCase.value.id,
      {
        valuation_report_version_id: latestReport.value.id,
        public_link: { expires_at: publicLinkExpiresAt.value },
      }
    );
    publicLinkUrl.value = data.public_url || '';
    useAlert('Link público do laudo criado.');
    loadCases();
  } catch {
    useAlert('Não foi possível criar o link público.');
  } finally {
    saving.value = false;
  }
};

const revokePublicLink = async () => {
  if (!canShareLatestReport.value) return;

  saving.value = true;
  try {
    await PropertyValuationCasesAPI.revokePublicLink(activeCase.value.id, {
      valuation_report_version_id: latestReport.value.id,
    });
    publicLinkUrl.value = '';
    useAlert('Link público do laudo revogado.');
    loadCases();
  } catch {
    useAlert('Não foi possível revogar o link público.');
  } finally {
    saving.value = false;
  }
};

watch(() => [props.imovelId, props.qprecoInterestId], loadPanel, {
  immediate: true,
});
</script>

<template>
  <LaisCard title="Revisão CMA e backtest">
    <div class="grid gap-4">
      <div
        v-if="loading"
        class="rounded-lais border border-lais-border-soft bg-lais-bg-muted px-4 py-6 text-[13px] text-lais-text-muted"
      >
        Carregando avaliação...
      </div>

      <div
        v-else-if="error"
        class="rounded-lais border border-rose-100 bg-rose-50 px-4 py-3 text-[13px] text-rose-800"
      >
        Avaliação indisponível no momento.
      </div>

      <div
        v-else-if="!activeCase"
        class="grid gap-3 rounded-lais border border-lais-border-soft bg-lais-bg-muted px-4 py-4 text-[13px]"
      >
        <p class="font-semibold text-lais-text">Nenhum caso CMA ativo.</p>
        <p class="text-lais-text-muted">
          A criação usa o motor local e gera snapshot versionado antes de
          qualquer revisão.
        </p>
        <div>
          <LaisPrimaryButton
            icon="i-lucide-file-plus-2"
            :disabled="!canCreate"
            :is-loading="saving"
            @click="createCase"
          >
            Criar avaliação
          </LaisPrimaryButton>
        </div>
      </div>

      <template v-else>
        <div class="grid gap-3 sm:grid-cols-4">
          <div class="rounded-lais-sm border border-lais-border-soft px-3 py-2">
            <p class="text-[11px] font-semibold uppercase text-lais-text-muted">
              Status
            </p>
            <p class="mt-1 text-[13px] font-semibold text-lais-text">
              {{ activeCase.status }}
            </p>
          </div>
          <div class="rounded-lais-sm border border-lais-border-soft px-3 py-2">
            <p class="text-[11px] font-semibold uppercase text-lais-text-muted">
              Versão
            </p>
            <p class="mt-1 text-[13px] font-semibold text-lais-text">
              {{ latestReport?.version_number || 'Sem laudo' }}
            </p>
          </div>
          <div class="rounded-lais-sm border border-lais-border-soft px-3 py-2">
            <p class="text-[11px] font-semibold uppercase text-lais-text-muted">
              Recomendado
            </p>
            <p class="mt-1 text-[13px] font-semibold text-lais-text">
              {{ formatCurrency(amounts.recommended_amount) }}
            </p>
          </div>
          <div class="rounded-lais-sm border border-lais-border-soft px-3 py-2">
            <p class="text-[11px] font-semibold uppercase text-lais-text-muted">
              Amostra
            </p>
            <p class="mt-1 text-[13px] font-semibold text-lais-text">
              {{ selections.included || 0 }}/{{ selections.total || 0 }}
            </p>
          </div>
        </div>

        <div
          class="grid gap-3 rounded-lais border border-lais-border-soft bg-white px-4 py-4"
        >
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p class="text-[13px] font-semibold text-lais-text">
                Laudo CMA atual
              </p>
              <p class="mt-1 text-[12px] text-lais-text-muted">
                Fonte: {{ activeCase.source_context?.source || 'CMA local' }} ·
                Data-base:
                {{ formatDate(activeCase.source_context?.data_date) }} · v{{
                  latestReport?.version_number || '0'
                }}
              </p>
            </div>
            <span
              class="inline-flex items-center gap-1 rounded-full bg-lais-primary-50 px-2.5 py-1 text-[11px] font-semibold text-lais-primary"
            >
              <span class="i-lucide-file-check-2 size-3.5" aria-hidden="true" />
              {{ activeCase.confidence?.level || 'sem confiança' }}
            </span>
          </div>

          <div class="grid gap-2 sm:grid-cols-3">
            <div
              v-for="percentile in [
                { label: 'P25', value: reportPercentiles.p25 },
                { label: 'P50', value: reportPercentiles.p50 },
                { label: 'P75', value: reportPercentiles.p75 },
              ]"
              :key="percentile.label"
              class="rounded-lais-sm border border-lais-border-soft bg-lais-bg-page px-3 py-2"
            >
              <p
                class="text-[11px] font-semibold uppercase text-lais-text-muted"
              >
                {{ percentile.label }}
              </p>
              <p class="mt-1 text-[15px] font-semibold text-lais-text">
                {{ formatCurrency(percentile.value) }}
              </p>
            </div>
          </div>

          <div v-if="criteriaEntries.length" class="flex flex-wrap gap-2">
            <span
              v-for="criterion in criteriaEntries"
              :key="criterion.label"
              class="rounded-full border border-lais-border-soft bg-lais-bg-page px-3 py-1.5 text-[12px] font-semibold text-lais-text"
            >
              {{ criterion.label }}: {{ criterion.value }}
            </span>
          </div>
        </div>

        <div
          class="grid gap-3 rounded-lais border border-lais-border-soft bg-white px-4 py-4"
        >
          <div class="flex flex-wrap items-center justify-between gap-3">
            <p class="text-[13px] font-semibold text-lais-text">
              Comparáveis e ajustes
            </p>
            <span class="text-[12px] font-semibold text-lais-text-muted">
              {{ reportComparables.length }} itens no laudo
            </span>
          </div>

          <div v-if="reportComparables.length" class="grid gap-2">
            <div
              v-for="item in reportComparables.slice(0, 8)"
              :key="
                item.observation_id ||
                item.id ||
                `${comparableSource(item)}-${comparableDate(item)}`
              "
              class="rounded-lais-sm border border-lais-border-soft bg-lais-bg-page px-3 py-2"
            >
              <div class="flex flex-wrap items-start justify-between gap-3">
                <div class="min-w-0">
                  <p class="text-[13px] font-semibold text-lais-text">
                    {{ formatCurrency(comparablePrice(item)) }}
                  </p>
                  <p class="mt-1 text-[12px] text-lais-text-muted">
                    {{ comparableSource(item) }} ·
                    {{ formatDate(comparableDate(item)) }} · score
                    {{ formatScore(comparableScore(item)) }}
                  </p>
                  <p
                    v-if="item.decision_reason"
                    class="mt-1 text-[12px] text-lais-text-muted"
                  >
                    Motivo: {{ item.decision_reason }}
                  </p>
                </div>
                <span
                  class="rounded-full px-2.5 py-1 text-[11px] font-semibold"
                  :class="
                    item.selection_status === 'included'
                      ? 'bg-emerald-50 text-emerald-800'
                      : 'bg-amber-50 text-amber-800'
                  "
                >
                  {{ item.selection_status || 'comparável' }}
                </span>
              </div>
              <p
                v-if="comparableAdjustment(item)"
                class="mt-2 text-[12px] text-lais-text-muted"
              >
                Ajuste aplicado:
                <strong class="text-lais-text">
                  {{ formatCurrency(comparableAdjustment(item)) }}
                </strong>
              </p>
            </div>
          </div>
          <p v-else class="text-[13px] text-lais-text-muted">
            Sem comparáveis materializados para este laudo.
          </p>
        </div>

        <div
          v-if="historyLoading"
          class="rounded-lais border border-lais-border-soft bg-lais-bg-muted px-4 py-3 text-[13px] text-lais-text-muted"
        >
          Carregando histórico CMA...
        </div>

        <div
          v-else-if="historyError"
          class="rounded-lais border border-amber-100 bg-amber-50 px-4 py-3 text-[13px] text-amber-900"
        >
          Histórico operacional indisponível.
        </div>

        <div
          v-else-if="history"
          class="grid gap-3 rounded-lais border border-lais-border-soft bg-white px-4 py-4"
        >
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p class="text-[13px] font-semibold text-lais-text">
                Histórico mensal versionado
              </p>
              <p class="mt-1 text-[12px] text-lais-text-muted">
                Publicação: manual · Provider externo:
                {{
                  providerMode.external_provider_called ? 'ativo' : 'desativado'
                }}
              </p>
            </div>
            <span
              class="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-800"
            >
              <span class="i-lucide-lock-keyhole size-3.5" aria-hidden="true" />
              {{
                autoPublishDisabled
                  ? 'Sem auto-publicação'
                  : 'Revisar publicação'
              }}
            </span>
          </div>

          <div class="grid gap-2 sm:grid-cols-3">
            <div
              v-for="month in visibleMonthlySeries"
              :key="month.month"
              class="rounded-lais-sm border border-lais-border-soft bg-lais-bg-page px-3 py-2"
            >
              <p
                class="text-[11px] font-semibold uppercase text-lais-text-muted"
              >
                {{ month.month }}
              </p>
              <p class="mt-1 text-[13px] font-semibold text-lais-text">
                {{ formatCurrency(month.average_recommended_amount) }}
              </p>
              <p class="mt-1 text-[12px] text-lais-text-muted">
                {{ month.report_versions_count || 0 }} versões ·
                {{ month.backtest_count || 0 }} backtests
              </p>
            </div>
          </div>

          <div class="grid gap-2 sm:grid-cols-3">
            <div
              class="rounded-lais-sm border border-lais-border-soft px-3 py-2"
            >
              <p
                class="text-[11px] font-semibold uppercase text-lais-text-muted"
              >
                Backtests
              </p>
              <p class="mt-1 text-[13px] font-semibold text-lais-text">
                {{ backtestSummary.count || 0 }}
              </p>
            </div>
            <div
              class="rounded-lais-sm border border-lais-border-soft px-3 py-2"
            >
              <p
                class="text-[11px] font-semibold uppercase text-lais-text-muted"
              >
                Erro médio
              </p>
              <p class="mt-1 text-[13px] font-semibold text-lais-text">
                {{ formatPercent(backtestSummary.average_absolute_error_pct) }}
              </p>
            </div>
            <div
              class="rounded-lais-sm border border-lais-border-soft px-3 py-2"
            >
              <p
                class="text-[11px] font-semibold uppercase text-lais-text-muted"
              >
                Última versão
              </p>
              <p class="mt-1 text-[13px] font-semibold text-lais-text">
                {{ versionHistory[0]?.version_number || 'Sem versão' }}
              </p>
            </div>
          </div>

          <div class="grid gap-2 sm:grid-cols-4">
            <div
              class="rounded-lais-sm border border-lais-border-soft bg-lais-bg-page px-3 py-2"
            >
              <p
                class="text-[11px] font-semibold uppercase text-lais-text-muted"
              >
                Calibração por mercado
              </p>
              <p class="mt-1 text-[13px] font-semibold text-lais-text">
                {{
                  calibrationReadiness.calibration_ready ? 'Pronta' : 'Pendente'
                }}
              </p>
            </div>
            <div
              class="rounded-lais-sm border border-lais-border-soft bg-lais-bg-page px-3 py-2"
            >
              <p
                class="text-[11px] font-semibold uppercase text-lais-text-muted"
              >
                Amostra calibrada
              </p>
              <p class="mt-1 text-[13px] font-semibold text-lais-text">
                {{ calibrationSummary.included_backtests_count || 0 }}/{{
                  calibrationSummary.documented_backtests_count || 0
                }}
              </p>
            </div>
            <div
              class="rounded-lais-sm border border-lais-border-soft bg-lais-bg-page px-3 py-2"
            >
              <p
                class="text-[11px] font-semibold uppercase text-lais-text-muted"
              >
                Erro calibrado
              </p>
              <p class="mt-1 text-[13px] font-semibold text-lais-text">
                {{
                  formatPercent(calibrationSummary.average_absolute_error_pct)
                }}
              </p>
            </div>
            <div
              class="rounded-lais-sm border border-lais-border-soft bg-lais-bg-page px-3 py-2"
            >
              <p
                class="text-[11px] font-semibold uppercase text-lais-text-muted"
              >
                Outliers
              </p>
              <p class="mt-1 text-[13px] font-semibold text-lais-text">
                {{ calibrationSummary.outlier_count || 0 }}
              </p>
            </div>
          </div>

          <div
            v-if="calibrationSegments.length"
            class="grid gap-2 rounded-lais-sm border border-lais-border-soft bg-lais-bg-muted px-3 py-2"
          >
            <p class="text-[12px] font-semibold text-lais-text">
              Segmento local
            </p>
            <div
              v-for="segment in calibrationSegments.slice(0, 2)"
              :key="segment.key"
              class="flex flex-wrap items-center justify-between gap-3 text-[12px]"
            >
              <span class="text-lais-text-muted">
                {{ segment.city }} · {{ segment.neighborhood }} ·
                {{ segment.property_type }}
              </span>
              <strong class="text-lais-text">
                {{ formatPercent(segment.average_absolute_error_pct) }}
              </strong>
            </div>
          </div>

          <div v-if="versionHistory.length" class="grid gap-2">
            <div
              v-for="version in versionHistory.slice(0, 4)"
              :key="version.id"
              class="flex flex-wrap items-center justify-between gap-3 border-t border-lais-border-soft pt-2 text-[12px]"
            >
              <span class="min-w-0 text-lais-text-muted">
                v{{ version.version_number }} · {{ version.report_kind }} ·
                {{ version.status }}
              </span>
              <strong class="text-lais-text">
                {{ formatCurrency(version.recommended_amount) }}
              </strong>
            </div>
          </div>
        </div>

        <div
          class="rounded-lais border border-lais-border-soft bg-lais-bg-muted px-4 py-3 text-[12px] text-lais-text-muted"
        >
          <p class="break-all">
            Checksum:
            <strong class="text-lais-text">{{ latestReport?.checksum }}</strong>
          </p>
          <p v-if="latestReview?.decision" class="mt-1">
            Última revisão:
            <strong class="text-lais-text">{{ latestReview.decision }}</strong>
          </p>
        </div>

        <div
          class="grid gap-3 rounded-lais border border-lais-border-soft px-4 py-4"
        >
          <div>
            <p class="text-[13px] font-semibold text-lais-text">
              Ajuste manual versionado
            </p>
            <p class="mt-1 text-[12px] text-lais-text-muted">
              Cada ajuste exige motivo, calcula diff e gera nova versão do
              laudo.
            </p>
          </div>

          <div
            v-if="latestAdjustment?.diff"
            class="rounded-lais-sm border border-emerald-100 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-900"
          >
            <p class="font-semibold">
              Último ajuste: {{ latestAdjustment.reason }}
            </p>
            <div
              v-for="(diff, field) in latestAdjustment.diff"
              :key="field"
              class="mt-1 flex flex-wrap gap-2"
            >
              <span>{{ field }}</span>
              <strong>
                {{ formatCurrency(diff.before) }} →
                {{ formatCurrency(diff.after) }}
              </strong>
              <span v-if="diff.delta">({{ formatCurrency(diff.delta) }})</span>
            </div>
          </div>

          <div class="grid gap-3 sm:grid-cols-[180px_1fr_auto]">
            <label
              class="grid gap-1 text-[12px] font-semibold text-lais-text-muted"
            >
              Valor ajustado
              <input
                v-model="adjustedAmount"
                inputmode="numeric"
                class="h-10 rounded-lais border border-lais-border px-3 text-[14px] font-normal text-lais-text"
              />
            </label>
            <label
              class="grid gap-1 text-[12px] font-semibold text-lais-text-muted"
            >
              Motivo do ajuste
              <input
                v-model="adjustmentReason"
                class="h-10 rounded-lais border border-lais-border px-3 text-[14px] font-normal text-lais-text"
              />
            </label>
            <div class="flex items-end">
              <LaisPrimaryButton
                icon="i-lucide-git-compare-arrows"
                :disabled="!canSubmitAdjustment"
                :is-loading="saving"
                @click="submitAdjustment"
              >
                Salvar ajuste
              </LaisPrimaryButton>
            </div>
          </div>
        </div>

        <div
          v-if="caseVersionHistory.length"
          class="grid gap-2 rounded-lais border border-lais-border-soft bg-white px-4 py-4"
        >
          <p class="text-[13px] font-semibold text-lais-text">
            Versões do laudo
          </p>
          <div
            v-for="version in caseVersionHistory.slice(0, 5)"
            :key="version.id"
            class="flex flex-wrap items-center justify-between gap-3 border-t border-lais-border-soft pt-2 text-[12px] first:border-t-0 first:pt-0"
          >
            <span class="text-lais-text-muted">
              v{{ version.version_number }} · {{ version.status }} ·
              {{ formatDate(version.issued_at) }}
            </span>
            <strong class="text-lais-text">
              {{ formatCurrency(version.recommended_amount) }}
            </strong>
          </div>
        </div>

        <div
          class="grid gap-3 rounded-lais border border-lais-border-soft px-4 py-4"
        >
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p class="text-[13px] font-semibold text-lais-text">
                Compartilhamento público
              </p>
              <p class="mt-1 text-[12px] text-lais-text-muted">
                Laudo redigido · token opaco · sem alterar preço publicado
              </p>
            </div>
            <span
              class="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold"
              :class="
                latestPublicLink.active
                  ? 'bg-emerald-50 text-emerald-800'
                  : 'bg-lais-bg-muted text-lais-text-muted'
              "
            >
              <span class="i-lucide-link-2 size-3.5" aria-hidden="true" />
              {{ latestPublicLink.active ? 'Ativo' : 'Sem link ativo' }}
            </span>
          </div>

          <div class="grid gap-3 sm:grid-cols-[1fr_auto_auto]">
            <label
              class="grid gap-1 text-[12px] font-semibold text-lais-text-muted"
            >
              Expira em
              <input
                v-model="publicLinkExpiresAt"
                type="datetime-local"
                class="h-10 rounded-lais border border-lais-border px-3 text-[14px] font-normal text-lais-text"
              />
            </label>
            <LaisPrimaryButton
              variant="ghost"
              icon="i-lucide-share-2"
              :disabled="!canShareLatestReport"
              :is-loading="saving"
              @click="createPublicLink"
            >
              Criar link
            </LaisPrimaryButton>
            <LaisPrimaryButton
              variant="ghost"
              icon="i-lucide-link-2-off"
              :disabled="!latestPublicLink.active || saving"
              :is-loading="saving"
              @click="revokePublicLink"
            >
              Revogar
            </LaisPrimaryButton>
          </div>

          <div
            v-if="publicLinkUrl"
            class="rounded-lais-sm border border-emerald-100 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-900"
          >
            <p class="break-all font-semibold">{{ publicLinkUrl }}</p>
          </div>

          <p
            v-if="latestPublicLink.token_last_four"
            class="text-[12px] text-lais-text-muted"
          >
            Últimos 4:
            <strong class="text-lais-text">
              {{ latestPublicLink.token_last_four }}
            </strong>
            · Expira:
            <strong class="text-lais-text">
              {{ latestPublicLink.expires_at || 'sem data' }}
            </strong>
          </p>
        </div>

        <div
          class="grid gap-3 rounded-lais border border-lais-border-soft px-4 py-4"
        >
          <div class="grid gap-3 sm:grid-cols-2">
            <label
              class="grid gap-1 text-[12px] font-semibold text-lais-text-muted"
            >
              Opinião do corretor
              <input
                v-model="brokerOpinionAmount"
                inputmode="numeric"
                class="h-10 rounded-lais border border-lais-border px-3 text-[14px] font-normal text-lais-text"
              />
            </label>
            <label
              class="grid gap-1 text-[12px] font-semibold text-lais-text-muted"
            >
              Aprovado pelo proprietário
              <input
                v-model="ownerApprovedAmount"
                inputmode="numeric"
                class="h-10 rounded-lais border border-lais-border px-3 text-[14px] font-normal text-lais-text"
              />
            </label>
          </div>
          <label
            class="grid gap-1 text-[12px] font-semibold text-lais-text-muted"
          >
            Motivo
            <textarea
              v-model="reviewReason"
              rows="2"
              class="rounded-lais border border-lais-border px-3 py-2 text-[14px] font-normal text-lais-text"
            />
          </label>
          <div class="flex flex-wrap justify-end gap-2">
            <LaisPrimaryButton
              variant="ghost"
              icon="i-lucide-clipboard-check"
              :is-loading="saving"
              @click="submitReview('reviewed')"
            >
              Registrar revisão
            </LaisPrimaryButton>
            <LaisPrimaryButton
              icon="i-lucide-badge-check"
              :is-loading="saving"
              @click="submitReview('approved')"
            >
              Aprovar valor
            </LaisPrimaryButton>
          </div>
        </div>

        <div
          class="grid gap-3 rounded-lais border border-lais-border-soft px-4 py-4"
        >
          <div class="grid gap-3 sm:grid-cols-3">
            <label
              class="grid gap-1 text-[12px] font-semibold text-lais-text-muted"
            >
              Valor observado
              <input
                v-model="actualAmount"
                inputmode="numeric"
                class="h-10 rounded-lais border border-lais-border px-3 text-[14px] font-normal text-lais-text"
              />
            </label>
            <label
              class="grid gap-1 text-[12px] font-semibold text-lais-text-muted"
            >
              Data do resultado
              <input
                v-model="outcomeDate"
                type="date"
                class="h-10 rounded-lais border border-lais-border px-3 text-[14px] font-normal text-lais-text"
              />
            </label>
            <label
              class="grid gap-1 text-[12px] font-semibold text-lais-text-muted"
            >
              Fonte
              <input
                v-model="outcomeSource"
                class="h-10 rounded-lais border border-lais-border px-3 text-[14px] font-normal text-lais-text"
              />
            </label>
          </div>
          <div class="flex justify-end">
            <LaisPrimaryButton
              variant="ghost"
              icon="i-lucide-activity"
              :is-loading="saving"
              @click="submitBacktest"
            >
              Registrar backtest
            </LaisPrimaryButton>
          </div>
        </div>
      </template>
    </div>
  </LaisCard>
</template>
